import React, { useCallback, useEffect, useState } from "react";
import { saveAs } from "file-saver";
import axios from "v2/utils/axios";
import { getAuthTokenFromCookie } from "lib/hooks";
import { LocalNotification } from "lib";
import { Button, EmptyState } from "components/Shared";
import { CheckBox, FieldHint } from "../shared";

/**
 * The company's USER CONTROLS — one row per address the company has granted
 * access to, whether or not an account exists for it yet. NOT the company's
 * accounts: a member with no control row does not appear here, because since
 * 17 Sep they cannot sign in by either method and there is nothing on this
 * screen to say about them. Adding their address is how they get in.
 *
 * The server owns the list (CompanyUserDirectory) and keys every row on the
 * EMAIL, not a record id: an address an admin pre-provisioned has no account id
 * to address, and the control's own id is not what a write is addressed by.
 * Saving is therefore always a POST to the same collection endpoint, which
 * upserts, and Delete is a DELETE on it with ?email=.
 *
 * Saving is explicit per row rather than on change. These are auth controls: a
 * mis-click on "Password login enabled?" taking effect immediately is the wrong
 * failure mode, a row with three edits would otherwise fire three requests and
 * three toasts, and every other section on this page is explicit-save.
 */

interface Row {
  // The server's id for this row: the account's id where there is one,
  // otherwise "control:<email>". NOT the email — two accounts in one company can
  // differ by case alone and normalise to the same address, so keying rows and
  // pending edits on the email would merge two people into one.
  key: string;
  email: string;
  full_name: string | null;
  // Whether the address has an ACCOUNT yet. Every row has a CONTROL by
  // definition, so there is no flag for that and Delete always has something to
  // act on.
  has_account: boolean;
  user_id: string | null;
  app_role: string;
  password_login_enabled: boolean;
  sso_login_enabled: boolean;
  last_seen_at: string | null;
}

// Adding somebody to a screen whose whole purpose is access means giving them
// access, so both methods start on. The checkboxes are right there to clear.
const BLANK_DRAFT = { app_role: "user", password_login_enabled: true, sso_login_enabled: true };

// The row exists and the person does not, which reads as a mistake until you
// know it is the normal state of a pre-provisioned address. Same sentence as the
// TCP admin dashboard's User Controls table, so the two screens explain it the
// same way.
const NO_ACCOUNT_HINT =
  "Nobody has signed in with this email yet. The account is created the first time they sign in with SSO.";

const endpoint = `${process.env.REACT_APP_API_ENDPOINT_V2}admin/user_controls`;

const UserControls: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loaded, setLoaded] = useState(false);
  // Per-row pending edits, keyed on email. A row is dirty while it has an entry.
  const [drafts, setDrafts] = useState<Record<string, Partial<Row>>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newDraft, setNewDraft] = useState<Partial<Row>>(BLANK_DRAFT);

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getAuthTokenFromCookie()}`,
  };

  // `settled` is the row whose save has just landed. Only ITS pending edits are
  // dropped — blanking the whole map would silently discard every other row's
  // unsaved changes and visibly snap their checkboxes back, which reads as the
  // save having reverted something.
  const load = useCallback((settled?: string) => {
    axios
      .get(endpoint, { headers: authHeaders })
      .then((res) => {
        const data = res.data?.data || [];
        setRows(data.map((entry: any) => ({ ...entry.attributes, key: entry.id } as Row)));
        if (settled) {
          setDrafts((current) => {
            const next = { ...current };
            delete next[settled];
            return next;
          });
        } else {
          setDrafts({});
        }
        setLoaded(true);
      })
      .catch((err) => {
        console.error("Error fetching user controls:", err);
        setLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => load(), [load]);

  const notifyError = (err: any, fallback: string) => {
    const detail = err?.response?.data?.errors?.detail;
    const message = Array.isArray(detail) ? detail.join(", ") : detail || fallback;
    new LocalNotification(message, message, "error");
  };

  const valueFor = (row: Row, field: keyof Row) => {
    const draft = drafts[row.key];
    return draft && field in draft ? (draft as any)[field] : (row as any)[field];
  };

  const edit = (row: Row, field: keyof Row, value: any) =>
    setDrafts((current) => ({ ...current, [row.key]: { ...current[row.key], [field]: value } }));

  const save = (row: Row) => {
    const draft = drafts[row.key];
    if (!draft) return;
    setSaving(row.key);

    axios
      .post(endpoint, { user_control: { email: row.email, ...draft } }, { headers: authHeaders })
      .then(() => {
        new LocalNotification("User settings saved");
        load(row.key);
      })
      .catch((err) => notifyError(err, "Could not save user settings"))
      .finally(() => setSaving(null));
  };

  const add = () => {
    const email = newEmail.trim();
    if (!email) return;
    setSaving(email);

    axios
      .post(endpoint, { user_control: { email, ...newDraft } }, { headers: authHeaders })
      .then(() => {
        new LocalNotification("User added");
        setNewEmail("");
        setNewDraft(BLANK_DRAFT);
        load();
      })
      .catch((err) => notifyError(err, "Could not add user"))
      .finally(() => setSaving(null));
  };

  // Deletes the CONTROL, not the account — but both login methods are gated on
  // that row, so afterwards the person cannot sign in at all. The confirm has to
  // say both halves: it is not "remove this person", and it is not harmless
  // either.
  const remove = (row: Row) => {
    const message =
      `Confirm delete the user control for ${row.email}? ` +
      "The account itself is not deleted, but they will no longer be able to sign in.";
    if (!window.confirm(message)) return;
    setSaving(row.key);

    axios
      .delete(endpoint, { headers: authHeaders, params: { email: row.email } })
      .then(() => {
        new LocalNotification("User control deleted");
        // Settled on THIS row's key, like save() does. A bare load() takes the
        // setDrafts({}) branch and silently discards every other row's unsaved
        // edits — see the comment on load. The deleted row is gone from the
        // response either way, so dropping its own draft is right.
        load(row.key);
      })
      .catch((err) => notifyError(err, "Could not delete the user control"))
      .finally(() => setSaving(null));
  };

  const downloadCsv = () => {
    axios
      .get(`${endpoint}/report`, {
        headers: authHeaders,
        // Top-level config, NOT inside `params` — axios only reads it here.
        responseType: "blob",
      })
      .then((res) => saveAs(new Blob([res.data], { type: "text/csv;charset=utf-8" }), "users.csv"))
      .catch((err) => {
        console.error("Error downloading users report:", err);
        new LocalNotification("Could not download the report", "Could not download the report", "error");
      });
  };

  return (
    <div className="px-5 py-4 bg-white">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="text-24 font-bold mb-0">User Controls</h2>
        <Button onClick={downloadCsv}>Download CSV</Button>
      </div>

      {/* The table ALWAYS renders, because the Add row lives inside it: a
          company whose last control was just deleted would otherwise be shown an
          empty state with no way to add anybody back. The empty state goes in a
          row of its own, above Add. */}
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>App role</th>
            <th className="text-center">Password login enabled?</th>
            <th className="text-center">SSO login enabled?</th>
            <th style={{ width: 170 }} />
          </tr>
        </thead>
        <tbody>
          {loaded && rows.length === 0 && (
            <tr>
              <td colSpan={6}>
                <EmptyState resource="users" />
              </td>
            </tr>
          )}

          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                <span className="d-flex align-items-center">
                  {row.full_name || <span className="text-muted">Unknown</span>}
                  {!row.has_account && (
                    <>
                      <span className="text-muted ml-2">(no account)</span>
                      {/* A question mark, not the default info circle: this
                          explains why something is MISSING rather than offering
                          guidance — the convention FieldHint's own docstring
                          sets. --below because this sits in a table BODY row,
                          where the default upward bubble opens over the column
                          headers. */}
                      <FieldHint
                        icon="fas fa-question-circle"
                        bubbleClass="field-hint__bubble--below"
                        text={NO_ACCOUNT_HINT}
                      />
                    </>
                  )}
                </span>
              </td>
              <td>{row.email}</td>
              <td>
                <select
                  className="form-control"
                  value={valueFor(row, "app_role")}
                  onChange={(e: any) => edit(row, "app_role", e.target.value)}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
              <td className="text-center">
                <CheckBox
                  checked={valueFor(row, "password_login_enabled")}
                  onChange={() => edit(row, "password_login_enabled", !valueFor(row, "password_login_enabled"))}
                />
              </td>
              <td className="text-center">
                <CheckBox
                  checked={valueFor(row, "sso_login_enabled")}
                  onChange={() => edit(row, "sso_login_enabled", !valueFor(row, "sso_login_enabled"))}
                />
              </td>
              <td>
                <div className="d-flex">
                  <Button onClick={() => save(row)} disabled={!drafts[row.key] || saving === row.key}>
                    Save
                  </Button>
                  <Button danger className="ml-2" onClick={() => remove(row)} disabled={saving === row.key}>
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}

          <tr>
            <td />
            <td>
              <input
                className="form-control"
                type="email"
                placeholder="name@example.com"
                value={newEmail}
                onChange={(e: any) => setNewEmail(e.target.value)}
              />
            </td>
            <td>
              <select
                className="form-control"
                value={newDraft.app_role}
                onChange={(e: any) => setNewDraft({ ...newDraft, app_role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </td>
            <td className="text-center">
              <CheckBox
                checked={!!newDraft.password_login_enabled}
                onChange={() => setNewDraft({ ...newDraft, password_login_enabled: !newDraft.password_login_enabled })}
              />
            </td>
            <td className="text-center">
              <CheckBox
                checked={!!newDraft.sso_login_enabled}
                onChange={() => setNewDraft({ ...newDraft, sso_login_enabled: !newDraft.sso_login_enabled })}
              />
            </td>
            <td>
              <Button onClick={add} disabled={!newEmail.trim() || saving === newEmail.trim()}>
                Add
              </Button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default UserControls;
